/**
 * Week view — seven-column time grid. Extracted verbatim (behavior-preserving)
 * from EventListModule's `renderWeekView`. Every value the original render
 * function read from component scope is now an explicit, fully-typed prop. The
 * component calls no hooks of its own, so the new boundary produces identical
 * output.
 *
 * getEventBg / getEventDotColor are pure lib helpers imported directly from
 * ../lib/eventColors rather than threaded through props.
 */
import React, { useState } from 'react';
import { AgentShimmer } from '@/common/components/AgentShimmer';
import { layoutOverlappingEvents } from '../event-layout';
import { parseEventDate, isAllDayish } from '../dateTimeUtils';
import { getEventBg, getEventDotColor } from '../lib/eventColors';
import { useCalendar24Hour, formatHourLabel, formatEventTime } from '../lib/timeFormat';
import { MobileLongPressEvent } from '../components/MobileLongPressEvent';
import { computeHourHeight } from '../hourHeightComputation';
import { DAY_GRID_START_HOUR, DAY_GRID_END_HOUR } from '../calendarConstants';
import type { EventObject } from '../eventTypes';

export interface WeekViewProps {
  calendarDate: Date;
  isMobile: boolean;
  isExpandedView: boolean;
  dayWeekBodyHeight: number;
  dayWeekContainerRef: React.RefObject<HTMLDivElement | null>;
  timeGridRef: React.RefObject<HTMLDivElement | null>;
  timeFilter: 'today' | 'week' | 'month';
  /** #3952: ids flagged as same-slot suspected duplicates → mark the chip. */
  suspectedDuplicateIds: Set<string>;
  getEventsForDate: (date: Date) => EventObject[];
  isToday: (date: Date) => boolean;
  setCalendarDate: React.Dispatch<React.SetStateAction<Date>>;
  setTimeFilterState: React.Dispatch<React.SetStateAction<'today' | 'week' | 'month'>>;
  handleEventClick: (event: EventObject) => void;
  handleCalendarSlotClick: (date: Date, hour: number) => void;
  onCompleteTaskEvent: (event: EventObject) => void;
  onOpenTaskEvent: (event: EventObject) => void;
}

export function WeekView({
  calendarDate,
  isMobile,
  isExpandedView,
  dayWeekBodyHeight,
  dayWeekContainerRef,
  timeGridRef,
  timeFilter,
  suspectedDuplicateIds,
  getEventsForDate,
  isToday,
  setCalendarDate,
  setTimeFilterState,
  handleEventClick,
  handleCalendarSlotClick,
  onCompleteTaskEvent,
  onOpenTaskEvent,
}: WeekViewProps) {
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  // Cap each day's all-day band so a busy day doesn't push the time grid down;
  // the overflow collapses behind a per-day "+N more" toggle.
  const MAX_ALLDAY = 3;
  const [expandedAllDayDays, setExpandedAllDayDays] = useState<Set<number>>(new Set());
  const toggleAllDayDay = (i: number) => setExpandedAllDayDays(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const startOfWeek = new Date(calendarDate);
  startOfWeek.setDate(calendarDate.getDate() - calendarDate.getDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  // #3534/#3585: grid bounds live in calendarConstants so the views and the
  // scroll-to-8 AM target (useCalendarNavigation) share one source of truth.
  const START_HOUR = DAY_GRID_START_HOUR;
  const END_HOUR = DAY_GRID_END_HOUR;
  const visibleHours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
  // #3619: scale per-hour height to fill the measured container instead of a
  // fixed value, so a tall window has no blank gap below the last hour. The
  // expanded base is the zoomed 96px, so the week stays zoomed-in and scrollable
  // on normal-height windows.
  const HOUR_HEIGHT = computeHourHeight(isMobile, isExpandedView, dayWeekBodyHeight, visibleHours.length);
  const gridHeight = visibleHours.length * HOUR_HEIGHT;

  const [use24Hour] = useCalendar24Hour();

  // Current time indicator position
  const now = new Date();
  const nowTop = ((now.getHours() - START_HOUR) + now.getMinutes() / 60) * HOUR_HEIGHT;
  // Clamp to half the dot height (4px) so translateY(-50%) never produces a
  // negative top value that gets clipped by the column's overflow-x:hidden.
  const nowTopSafe = Math.max(4, nowTop);
  const showNowLine = now.getHours() >= START_HOUR && now.getHours() < END_HOUR;

  // #1541: see renderDayView for rationale — fill available height on first paint.
  const bodyHeight = dayWeekBodyHeight > 0 ? dayWeekBodyHeight : 12 * HOUR_HEIGHT;

  return (
    <div className="xv-week" ref={dayWeekContainerRef}>
      {/* Day column headers */}
      <div className="xv-week-header">
        <div className="xv-week-header-gutter" />
        {weekDays.map((day, i) => {
          const isTodayDate = isToday(day);
          return (
            <div key={i} className="xv-week-day-col">
              <div className="xv-week-day-name">
                {dayNames[i]}
              </div>
              <div
                className={`xv-week-day-num ${isTodayDate ? 'today-num' : ''}`}
                onClick={() => { setCalendarDate(new Date(day)); setTimeFilterState('today'); }}
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day events row */}
      {(() => {
        const hasAnyAllDay = weekDays.some(day => getEventsForDate(day).some(e => isAllDayish(e)));
        if (!hasAnyAllDay) return null;
        return (
          <div className="xv-week-allday">
            <div className="xv-week-header-gutter" />
            {weekDays.map((day, i) => {
              const allDayEvts = getEventsForDate(day).filter(e => isAllDayish(e));
              const dayExpanded = expandedAllDayDays.has(i);
              const visibleAllDayEvts = dayExpanded ? allDayEvts : allDayEvts.slice(0, MAX_ALLDAY);
              const hiddenAllDayCount = allDayEvts.length - visibleAllDayEvts.length;
              return (
                <div key={i} className="xv-week-allday-col">
                  {visibleAllDayEvts.map(event => {
                    const wkAllDayAccentColor = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
                    const wkAllDayStyle = { position: 'relative' as const, overflow: 'hidden', boxSizing: 'border-box' as const, background: getEventBg(event.eventType, true, event.metadata?.customColor), borderLeft: `3px solid ${wkAllDayAccentColor}` };
                    // #3952: same-slot suspected-duplicate marker on all-day chips.
                    const wkAllDayDupClass = suspectedDuplicateIds.has(event.id) ? ' gcal-event--dup' : '';
                    // #3951: outbound-push failure marker on all-day chips.
                    const wkAllDaySyncErrorClass = event.providerSyncState === 'error' ? ' gcal-event--sync-error' : '';
                    const wkAllDaySyncErrorTitle = event.providerSyncState === 'error' ? ' — not synced to Google (push failed; open to retry)' : '';
                    return isMobile ? (
                      <MobileLongPressEvent
                        key={event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id}
                        className={`gcal-event gcal-event-allday${wkAllDayDupClass}${wkAllDaySyncErrorClass}`}
                        style={wkAllDayStyle}
                        onLongPress={() => (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event)}
                      >
                        {event._sourceTaskId && (
                          <button
                            type="button"
                            className="xv-task-event-check"
                            aria-label={`Complete ${event.title}`}
                            title="Mark complete"
                            onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </button>
                        )}
                        <AgentShimmer objectId={event.id} />
                        <span className="gcal-event-title">{event.title}</span>
                      </MobileLongPressEvent>
                    ) : (
                      <div
                        key={event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id}
                        className={`gcal-event gcal-event-allday${wkAllDayDupClass}${wkAllDaySyncErrorClass}`}
                        style={wkAllDayStyle}
                        onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); } }}
                        role="button"
                        tabIndex={0}
                        aria-label={`${event.title}${wkAllDaySyncErrorTitle}`}
                        title={`${event.title}${wkAllDaySyncErrorTitle}`}
                      >
                        {event._sourceTaskId && (
                          <button
                            type="button"
                            className="xv-task-event-check"
                            aria-label={`Complete ${event.title}`}
                            title="Mark complete"
                            onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </button>
                        )}
                        <AgentShimmer objectId={event.id} />
                        <span className="gcal-event-title">{event.title}</span>
                      </div>
                    );
                  })}
                  {(hiddenAllDayCount > 0 || dayExpanded) && (
                    <button
                      type="button"
                      className="xv-allday-more"
                      onClick={(e) => { e.stopPropagation(); toggleAllDayDay(i); }}
                    >
                      {dayExpanded ? 'Show less' : `+${hiddenAllDayCount} more`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Scrollable time grid */}
      <div ref={timeFilter === 'week' ? timeGridRef : undefined} className="xv-week-body" style={{ height: bodyHeight, flex: 'none' }}>
        {/* Time gutter */}
        <div className="xv-time-gutter" style={{ height: gridHeight }}>
          {visibleHours.map((h, i) => (
            <div key={h} className="xv-time-label" style={{ top: i * HOUR_HEIGHT }}>
              {formatHourLabel(h, use24Hour)}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {weekDays.map((day, di) => {
          const dayEvents = getEventsForDate(day).filter(e => !isAllDayish(e));
          const isTodayDate = isToday(day);

          return (
            <div key={di} className={`xv-day-column ${isTodayDate ? 'is-today' : ''}`} style={{ height: gridHeight }}>
              {/* Hour lines */}
              {visibleHours.map((h, i) => (
                <React.Fragment key={h}>
                  <div className="xv-hour-line" style={{ top: i * HOUR_HEIGHT }} />
                  <div className="xv-half-hour-line" style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
                </React.Fragment>
              ))}

              {/* Click targets for adding events */}
              {visibleHours.map((h, i) => (
                <div
                  key={`slot-${h}`}
                  style={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, height: HOUR_HEIGHT, cursor: 'pointer' }}
                  onClick={() => handleCalendarSlotClick(day, h)}
                />
              ))}

              {/* Current time indicator */}
              {isTodayDate && showNowLine && (
                <>
                  <div className="xv-now-dot" style={{ top: nowTopSafe }} />
                  <div className="xv-now-line" style={{ top: nowTopSafe }} />
                </>
              )}

              {/* Events — overlapping events laid out side-by-side (#1473) */}
              {layoutOverlappingEvents(dayEvents).map(({ event, lane, columns }) => {
                const startTime = parseEventDate(event.startAt) ?? new Date();
                const endTime = event.endAt ? (parseEventDate(event.endAt) ?? startTime) : new Date(startTime.getTime() + 60 * 60 * 1000);
                const durationMin = Math.max((endTime.getTime() - startTime.getTime()) / 60000, 0);
                const top = ((startTime.getHours() - START_HOUR) + startTime.getMinutes() / 60) * HOUR_HEIGHT;
                // Clamp to the end of the day so events running past midnight
                // don't overflow below the grid and inflate the scroll height.
                const dayEndPx = visibleHours.length * HOUR_HEIGHT;
                // Task-derived events are a zero-duration instant; give them a
                // taller floor (vs the 18px minimum) so the single-row title +
                // time has padding above and below and stays readable. Week
                // columns are narrower, so a slightly shorter floor than day view.
                const minHeight = event._sourceTaskId ? 26 : 18;
                const height = Math.min(Math.max(durationMin / 60 * HOUR_HEIGHT, minHeight), Math.max(0, dayEndPx - top));
                const widthPct = 100 / columns;
                const leftPct = lane * widthPct;
                // Smaller gap in week view (columns are narrower)
                const gapPct = columns > 1 ? 1 : 0;
                // Only the rightmost column needs to clear the scrollbar; the
                // others fill to their divider. Matches the line inset.
                const RIGHT_INSET = di === weekDays.length - 1 ? 13 : 0;
                // Task-derived events are a single specific moment (zero
                // duration) — show one time, not a start–end range.
                const timeRange = event._sourceTaskId
                  ? formatEventTime(startTime, use24Hour)
                  : `${formatEventTime(startTime, use24Hour)} - ${formatEventTime(endTime, use24Hour)}`;
                // Short bubbles can't fit the stacked title + time without
                // clipping — switch to a single row (title ellipsis + time range
                // pinned right) so neither gets cut off.
                const compact = height < 34;
                const eventKey = event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id;
                // #3952: same-slot suspected-duplicate marker; click opens the modal.
                const isDup = suspectedDuplicateIds.has(event.id);
                const dupClass = isDup ? ' xv-week-event--dup' : '';
                const dupTitle = isDup ? ' — possible duplicate (same time slot, similar title)' : '';
                // #3951: outbound-push failure marker (see DayView). Click opens
                // the editor's "Retry sync" affordance.
                const isSyncError = event.providerSyncState === 'error';
                const syncErrorClass = isSyncError ? ' xv-week-event--sync-error' : '';
                const syncErrorTitle = isSyncError ? ' — not synced to Google (push failed; open to retry)' : '';
                const weekSourceAccentColor = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
                const laneStyle: React.CSSProperties = {
                  top,
                  height,
                  left: `calc((100% - ${RIGHT_INSET}px) * ${leftPct / 100} + ${lane > 0 ? gapPct / 2 : 0}px)`,
                  right: 'auto',
                  width: `calc((100% - ${RIGHT_INSET}px) * ${widthPct / 100} - ${gapPct}px)`,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  background: getEventBg(event.eventType, false, event.metadata?.customColor),
                  // Source indicator: left border keyed off calendar color (Google) or
                  // slate (#64748b) for Xavior-native events. Closes #2836.
                  borderLeft: `3px solid ${weekSourceAccentColor}`,
                };

                const weekSourceLabel = event.providerCalendarId ? (event.providerCalendarName || 'Calendar') : 'Xavior';
                const weekAriaLabel = `${event.title}, ${timeRange}, ${weekSourceLabel}`;

                return isMobile ? (
                  <MobileLongPressEvent
                    key={eventKey}
                    eventId={event.id}
                    className={`xv-week-event xv-week-event--week${compact ? ' xv-week-event--compact' : ''}${event._sourceTaskId ? ' xv-week-event--task' : ''}${dupClass}${syncErrorClass}`}
                    style={laneStyle}
                    aria-label={`${weekAriaLabel}${syncErrorTitle}`}
                    onLongPress={() => (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event)}
                  >
                    {event._sourceTaskId && (
                      <button
                        type="button"
                        className="xv-task-event-check"
                        aria-label={`Complete ${event.title}`}
                        title="Mark complete"
                        onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </button>
                    )}
                    <AgentShimmer objectId={event.id} />
                    <span className="xv-week-event-label">{event.title}</span>
                    <span className="xv-week-event-time">{timeRange}</span>
                  </MobileLongPressEvent>
                ) : (
                  <div
                    key={eventKey}
                    data-event-id={event.id}
                    className={`xv-week-event xv-week-event--week${compact ? ' xv-week-event--compact' : ''}${event._sourceTaskId ? ' xv-week-event--task' : ''}${dupClass}${syncErrorClass}`}
                    style={laneStyle}
                    role="button"
                    tabIndex={0}
                    aria-label={`${weekAriaLabel}${dupTitle}${syncErrorTitle}`}
                    onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                    title={`${event.title}${dupTitle}${syncErrorTitle}`}
                  >
                    {event._sourceTaskId && (
                      <button
                        type="button"
                        className="xv-task-event-check"
                        aria-label={`Complete ${event.title}`}
                        title="Mark complete"
                        onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </button>
                    )}
                    <AgentShimmer objectId={event.id} />
                    <span className="xv-week-event-label">{event.title}</span>
                    <span className="xv-week-event-time">{timeRange}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
